import type { LevelDefinition } from "@/domain";

export interface SoundBubbleChoice {
  id: string;
  label: string;
}

export interface SoundBubblePayload {
  spokenText: string;
  soundAudio: string;
  question: string;
  choices: SoundBubbleChoice[];
  correctChoiceId: string;
  hint: string;
}

export const soundBubbleLevels: LevelDefinition<SoundBubblePayload>[] = [
  {
    id: "chinese-sound-bubbles-1",
    domain: "chinese",
    activityType: "sound-bubbles",
    difficulty: 1,
    objectives: ["辨认简单拼音音节", "用耳朵比较不同的开头音"],
    title: "听见 mā",
    instruction: "泡泡里藏着一个声音。听一听，找到和它一样的拼音。",
    promptAudio: "/audio/chinese/sound-bubbles/1.ogg",
    assets: [],
    payload: {
      spokenText: "mā",
      soundAudio: "/audio/chinese/sound-bubbles/stimulus-1.ogg",
      question: "你听到的是哪一个拼音？",
      choices: [
        { id: "ma", label: "mā" },
        { id: "ba", label: "bā" }
      ],
      correctChoiceId: "ma",
      hint: "把声音放慢听一遍，开头的音会来帮忙。"
    }
  },
  {
    id: "chinese-sound-bubbles-2",
    domain: "chinese",
    activityType: "sound-bubbles",
    difficulty: 2,
    objectives: ["分辨相近的简单音节", "把听到的声音和拼音连起来"],
    title: "小兔的 tù",
    instruction: "小兔带来一个新声音。听一听，哪一个拼音和它一样？",
    promptAudio: "/audio/chinese/sound-bubbles/2.ogg",
    assets: [],
    payload: {
      spokenText: "tù",
      soundAudio: "/audio/chinese/sound-bubbles/stimulus-2.ogg",
      question: "小兔带来的是哪一个拼音？",
      choices: [
        { id: "tu", label: "tù" },
        { id: "du", label: "dù" },
        { id: "mu", label: "mù" }
      ],
      correctChoiceId: "tu",
      hint: "再听听第一个音，它会把答案轻轻带出来。"
    }
  },
  {
    id: "chinese-sound-bubbles-3",
    domain: "chinese",
    activityType: "sound-bubbles",
    difficulty: 3,
    objectives: ["辨认常见声调", "比较同一音节的声音变化"],
    title: "小马的 mǎ",
    instruction: "声音也会拐弯呢。听一听，找出声调一样的拼音。",
    promptAudio: "/audio/chinese/sound-bubbles/3.ogg",
    assets: [],
    payload: {
      spokenText: "mǎ",
      soundAudio: "/audio/chinese/sound-bubbles/stimulus-3.ogg",
      question: "哪个 ma 的声调和你听到的一样？",
      choices: [
        { id: "first-tone", label: "mā" },
        { id: "second-tone", label: "má" },
        { id: "third-tone", label: "mǎ" },
        { id: "fourth-tone", label: "mà" }
      ],
      correctChoiceId: "third-tone",
      hint: "听听声音的小弯弯：先低下来，再往上走。"
    }
  },
  {
    id: "chinese-sound-bubbles-4",
    domain: "chinese",
    activityType: "sound-bubbles",
    difficulty: 4,
    objectives: ["分辨同一音节的四个声调", "把听到的声调和拼音对应起来"],
    title: "听见 bǎo",
    instruction: "这个声音有一个小拐弯。听一听，是哪一个 bǎo？",
    promptAudio: "/audio/chinese/sound-bubbles/4.ogg",
    assets: [],
    payload: {
      spokenText: "bǎo",
      soundAudio: "/audio/chinese/sound-bubbles/stimulus-4.ogg",
      question: "你听到的是哪一个 bǎo？",
      choices: [
        { id: "first-tone", label: "bāo" },
        { id: "second-tone", label: "báo" },
        { id: "third-tone", label: "bǎo" },
        { id: "fourth-tone", label: "bào" }
      ],
      correctChoiceId: "third-tone",
      hint: "听听声音的弯弯：先低下来，再往上走。"
    }
  },
  {
    id: "chinese-sound-bubbles-5",
    domain: "chinese",
    activityType: "sound-bubbles",
    difficulty: 5,
    objectives: ["分辨相近的声母和声调", "在四个相近音节中找到目标音"],
    title: "小桥的 qiáo",
    instruction: "小桥带来一个新声音。慢慢听，哪一个拼音和它一样？",
    promptAudio: "/audio/chinese/sound-bubbles/5.ogg",
    assets: [],
    payload: {
      spokenText: "qiáo",
      soundAudio: "/audio/chinese/sound-bubbles/stimulus-5.ogg",
      question: "小桥的 qiáo 是哪一个？",
      choices: [
        { id: "qiao-second", label: "qiáo" },
        { id: "qiao-third", label: "qiǎo" },
        { id: "jiao-fourth", label: "jiào" },
        { id: "xiao-third", label: "xiǎo" }
      ],
      correctChoiceId: "qiao-second",
      hint: "先听开头的 q，再听声音往上扬。"
    }
  },
  {
    id: "chinese-sound-bubbles-6",
    domain: "chinese",
    activityType: "sound-bubbles",
    difficulty: 6,
    objectives: ["听辨两个音节组成的词", "比较相近声母和声调的组合"],
    title: "听见 xué xiào",
    instruction: "这次有两个音节。听完整，再找到一样的拼音。",
    promptAudio: "/audio/chinese/sound-bubbles/6.ogg",
    assets: [],
    payload: {
      spokenText: "xué xiào",
      soundAudio: "/audio/chinese/sound-bubbles/stimulus-6.ogg",
      question: "你听到的是哪一组拼音？",
      choices: [
        { id: "school", label: "xué xiào" },
        { id: "snow-school", label: "xuě xiào" },
        { id: "learn-teach", label: "xué jiào" },
        { id: "shoe-school", label: "xié xiào" }
      ],
      correctChoiceId: "school",
      hint: "分成两小段听：先是 xué，再是 xiào。"
    }
  }
];
